import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAdminSubmissions,
  useForceApproveSubmission,
  useForceRejectSubmission,
  useWithdrawSubmission,
} from '../useAdmin';
import type { AdminSubmissionRow, ListSubmissionsParams, SubmissionStatus } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminSubmissions.css';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: '' | SubmissionStatus; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'pending', label: 'Pending' },
  { value: 'awaiting_parent_consent', label: 'Awaiting parent consent' },
  { value: 'awaiting_teacher_review', label: 'Awaiting teacher review' },
  { value: 'awaiting_moderator', label: 'Awaiting moderator' },
  { value: 'processing', label: 'Processing' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const VISIBILITY_OPTIONS: {
  value: '' | 'public' | 'class_only';
  label: string;
}[] = [
  { value: '', label: 'Any visibility' },
  { value: 'public', label: 'Public' },
  { value: 'class_only', label: 'Class only' },
];

/**
 * Admin submissions oversight (F7). Search/filter the queue and
 * apply per-row overrides (force-approve / force-reject / withdraw).
 */
export function AdminSubmissions(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<'' | SubmissionStatus>('');
  const [visibility, setVisibility] = useState<'' | 'public' | 'class_only'>('');
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [page, setPage] = useState(0);

  const params = useMemo<ListSubmissionsParams>(
    () => ({
      status: statusFilter || undefined,
      visibility: visibility || undefined,
      submitter_email: submitterEmail.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [statusFilter, visibility, submitterEmail, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminSubmissions(params);
  const approve = useForceApproveSubmission();
  const reject = useForceRejectSubmission();
  const withdraw = useWithdrawSubmission();
  const lastError = approve.error || reject.error || withdraw.error;

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-subs" aria-label="Submissions oversight">
      <header className="bl-admin-subs__header">
        <div>
          <h2>Submissions</h2>
          <p className="bl-admin-subs__sub">
            Every gallery submission. Click a project name to open the full moderator view (audit
            chain, blobs, reports). The buttons here are admin-side overrides for stuck or contested
            decisions — use them when you don&rsquo;t need to re-review.
          </p>
        </div>
        <p className="bl-admin-subs__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No submissions match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form className="bl-admin-subs__filters" onSubmit={(e) => e.preventDefault()} role="search">
        <label className="bl-admin-subs__field">
          <span className="bl-admin-subs__field-label">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => reset(setStatusFilter)(e.target.value as '' | SubmissionStatus)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-subs__field">
          <span className="bl-admin-subs__field-label">Visibility</span>
          <select
            value={visibility}
            onChange={(e) => reset(setVisibility)(e.target.value as '' | 'public' | 'class_only')}
          >
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-subs__field">
          <span className="bl-admin-subs__field-label">Submitter email</span>
          <input
            type="search"
            value={submitterEmail}
            onChange={(e) => reset(setSubmitterEmail)(e.target.value)}
            placeholder="ada@school.edu"
            autoComplete="off"
          />
        </label>
      </form>

      {(isError || lastError) && (
        <p className="bl-admin-subs__error" role="alert">
          {lastError instanceof AuthApiError
            ? lastError.detail || lastError.message
            : (error || lastError) instanceof Error
              ? ((error || lastError) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <div className="bl-admin-subs__table-wrap">
        <table className="bl-admin-subs__table">
          <thead>
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Submitter</th>
              <th scope="col">Status</th>
              <th scope="col">Visibility</th>
              <th scope="col">When</th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((row) => (
              <SubmissionRow
                key={row.id}
                row={row}
                onApprove={() => {
                  const note = window.prompt('Optional note for the audit log:', '');
                  approve.mutate({
                    id: row.id,
                    note: note?.trim() || undefined,
                  });
                }}
                onReject={() => {
                  const reason = window.prompt('Reason for rejection (shown to the kid):');
                  if (!reason || !reason.trim()) return;
                  reject.mutate({ id: row.id, reason: reason.trim() });
                }}
                onWithdraw={() => {
                  if (
                    window.confirm(
                      'Withdraw this submission so it leaves the queue without counting as a rejection?',
                    )
                  ) {
                    withdraw.mutate({ id: row.id });
                  }
                }}
                busy={approve.isPending || reject.isPending || withdraw.isPending}
              />
            ))}
            {!isLoading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="bl-admin-subs__empty">
                  No submissions match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <nav className="bl-admin-subs__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-subs__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function SubmissionRow({
  row,
  onApprove,
  onReject,
  onWithdraw,
  busy,
}: {
  row: AdminSubmissionRow;
  onApprove: () => void;
  onReject: () => void;
  onWithdraw: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <tr data-status={row.status}>
      <td>
        {/* The project name is the click-through to the full moderator
            view — audit chain, blobs, reports, the works. Admins
            shouldn't have to Approve/Reject blind. */}
        <Link
          to={`/admin/moderator/${row.id}`}
          className="bl-admin-subs__project bl-admin-subs__project--link"
          title="Open the full moderator view for this submission"
        >
          {row.project_name || '(untitled)'}
        </Link>
        <div className="bl-admin-subs__sub-meta">
          {row.project_kind || '—'} · #{row.id.slice(0, 8)}
        </div>
      </td>
      <td>{row.submitter_email || '(unknown)'}</td>
      <td>
        <span className={`bl-admin-subs__status bl-admin-subs__status--${row.status}`}>
          {row.status.replace(/_/g, ' ')}
        </span>
        {row.rejection_reason && (
          <div className="bl-admin-subs__reason">{row.rejection_reason}</div>
        )}
      </td>
      <td>
        {row.visibility === 'public' ? (
          <span className="bl-admin-subs__vis-public">Public</span>
        ) : (
          <span className="bl-admin-subs__vis-class">Class only</span>
        )}
      </td>
      <td>
        <time dateTime={row.created_at}>{formatDate(row.created_at)}</time>
      </td>
      <td>
        <div className="bl-admin-subs__row-actions">
          {row.status !== 'approved' && (
            <button type="button" onClick={onApprove} disabled={busy}>
              Approve
            </button>
          )}
          {row.status !== 'rejected' && (
            <button
              type="button"
              onClick={onReject}
              disabled={busy}
              className="bl-admin-subs__row-action--warn"
            >
              Reject
            </button>
          )}
          {row.status !== 'withdrawn' && (
            <button type="button" onClick={onWithdraw} disabled={busy}>
              Withdraw
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
